import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal, PageShell, EASE } from "@/components/Reveal";

const FotoCredit = ({ testid }) => (
  <p data-testid={testid} className="mt-3 text-xs tracking-wide text-anthrazit/50">
    Foto: Sabine Brauns
  </p>
);

export default function Yoga() {
  return (
    <PageShell testid="yoga-page">
      <PageHero
        title="Yoga"
        subtitle="Kraft, Beweglichkeit und innere Ruhe"
        testid="yoga-hero"
      />

      <section data-testid="yoga-text" className="px-6 pb-16 md:px-12 md:pb-24">
        <div className="mx-auto max-w-3xl">
          <Reveal>
            <p className="text-base leading-loose text-anthrazit/85 md:text-lg">
              Du möchtest dich wieder wohler in deinem Körper fühlen, beweglicher werden und
              gleichzeitig zur Ruhe kommen? Dein Alltag fordert viel von dir und du wünschst dir
              einen Ort, an dem du durchatmen, neue Kraft sammeln und wieder mehr bei dir sein
              kannst?
            </p>
          </Reveal>
          <Reveal delay={0.05} className="mt-8">
            <p className="text-base leading-loose text-anthrazit/85 md:text-lg">
              In meinen Yogastunden verbinden sich achtsame Bewegung, Kräftigung und Dehnung mit
              bewusstem Atem und Momenten der Stille. Spannungen können sich lösen, dein Körper
              wird beweglicher und kräftiger und dein Nervensystem bekommt die Möglichkeit,
              herunterzufahren.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <p
              data-testid="yoga-statement"
              className="my-12 border-l-2 border-rot pl-7 font-serif text-2xl leading-snug text-rot md:my-16 md:pl-10 md:text-[2rem]"
            >
              Yoga bedeutet für mich, dich selbst wieder zu spüren – mit mehr Ruhe, Kraft und
              Lebendigkeit im eigenen Körper.
            </p>
          </Reveal>
        </div>
      </section>

      <section data-testid="yoga-galerie" className="px-6 pb-16 md:px-12 md:pb-24">
        <div className="mx-auto max-w-6xl space-y-6 md:space-y-8">
          <div className="grid items-start gap-6 sm:grid-cols-2 md:gap-8">
            <Reveal>
              <div className="overflow-hidden rounded-[2rem]">
                <motion.img
                  src="/images/yoga-meditation.png"
                  alt="Gruppe meditiert sitzend mit geschlossenen Augen in einer Wiese"
                  className="block h-auto w-full"
                  initial={{ scale: 1.06 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 1.6, ease: EASE }}
                />
              </div>
              <FotoCredit testid="yoga-credit-meditation" />
            </Reveal>
            <Reveal delay={0.1}>
              <div className="overflow-hidden rounded-[2rem]">
                <motion.img
                  src="/images/yoga-dock.webp"
                  alt="Drei Frauen sitzen auf einem Holzsteg und blicken gemeinsam ins Grüne"
                  className="block h-auto w-full"
                  initial={{ scale: 1.06 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 1.6, ease: EASE }}
                />
              </div>
              <FotoCredit testid="yoga-credit-dock" />
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <div className="overflow-hidden rounded-[2rem]">
              <motion.img
                src="/images/yoga-gruppe.webp"
                alt="Drei Frauen sitzen lachend in einer Wiese nach der Yogastunde"
                className="block h-auto w-full"
                initial={{ scale: 1.06 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 1.6, ease: EASE }}
              />
            </div>
            <FotoCredit testid="yoga-credit-gruppe" />
          </Reveal>
        </div>
      </section>

      <section className="px-6 pb-24 md:px-12 md:pb-36">
        <div className="mx-auto max-w-3xl">
          <Reveal>
            <Link
              to="/kontakt"
              data-testid="yoga-kontakt-link"
              className="group inline-flex items-center gap-4 text-sm uppercase tracking-[0.3em] text-rot"
            >
              <span className="border-b border-rot/40 pb-1 transition-colors duration-500 group-hover:border-rot">
                Kontakt
              </span>
              <ArrowUpRight
                size={18}
                strokeWidth={1.5}
                className="transition-transform duration-500 group-hover:rotate-45"
              />
            </Link>
          </Reveal>
        </div>
      </section>
    </PageShell>
  );
}
